import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// DocumentStatusWindow.tsx -- per-document digest progress window.
//
// Workbench I18 (C.7). Renders a small floating window that shows
// the digest pipeline state for one document (extract -> chunk ->
// embed). Polls GET /documents/:id/status every 30s while open AND
// the digest is in flight; stops polling when overall reaches
// 'complete' or 'failed' (the data is final at that point).
//
// User direction:
//   "have a graphical type stage showing what stage the document
//    is in and details of the digest while processing and getting
//    regular updates (every 30 seconds) while the window is open
//    so they know when it's done processing."
//   "a user can minimize it to a pill or close it and re-open it
//    with the status by clicking on the file"
//
// Two render modes:
//   - Full window (minimized: false): header + stage list + output
//   - Pill (minimized: true): compact bottom-strip badge with status
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../utils/api.js';
import { useProjectContext } from '../../hooks/useAgent.js';
import { useDocumentStatusStore, useDocumentStatusWindow } from '../../state/documentStatusStore.js';
const POLL_INTERVAL_MS = 30_000;
const PILL_W = 220;
const PILL_H = 36;
const PILL_GAP = 8;
const PILL_BOTTOM_INSET = 16;
const PILL_RIGHT_INSET = 24;
function pillPose(idx) {
    return {
        right: PILL_RIGHT_INSET + idx * (PILL_W + PILL_GAP),
        bottom: PILL_BOTTOM_INSET,
    };
}
const WINDOW_W = 380;
export function DocumentStatusWindow({ documentId, pillIndex }) {
    const { projectId } = useProjectContext();
    const win = useDocumentStatusWindow(documentId);
    const closeWindow = useDocumentStatusStore((s) => s.closeWindow);
    const setMinimized = useDocumentStatusStore((s) => s.setMinimized);
    const setPose = useDocumentStatusStore((s) => s.setPose);
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(false);
    const lastFetchAt = useRef(0);
    // Drag state for the full window. Mirrors AssistantWindow's pattern:
    // header onMouseDown captures origin + window width; document-level
    // onMouseMove updates the store's pose; mouseup tears down.
    const dragStateRef = useRef(null);
    // Default pose when the user hasn't dragged: pin to bottom-right
    // (under the chat-window pill strip, with a small inset). This is
    // computed once on mount; once the user drags, win.pose takes over.
    const defaultPose = useMemo(() => {
        if (typeof window === 'undefined')
            return { x: 100, y: 100 };
        return {
            x: window.innerWidth - WINDOW_W - 24,
            y: Math.max(24, window.innerHeight - 480),
        };
    }, []);
    const pose = win?.pose ?? defaultPose;
    const onHeaderMouseDown = useCallback((e) => {
        // Skip drag when the click is on a control button (minimize/close).
        if (e.target.closest('button'))
            return;
        dragStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            origX: pose.x,
            origY: pose.y,
        };
        const onMove = (ev) => {
            const s = dragStateRef.current;
            if (!s)
                return;
            // Clamp to viewport: 8px gutters, leave 80px clear at the
            // bottom for the system pill strip / member dock.
            const x = Math.max(8, Math.min(window.innerWidth - WINDOW_W - 8, s.origX + (ev.clientX - s.startX)));
            const y = Math.max(8, Math.min(window.innerHeight - 80, s.origY + (ev.clientY - s.startY)));
            setPose(documentId, { x, y });
        };
        const onUp = () => {
            dragStateRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [documentId, pose.x, pose.y, setPose]);
    // Compute the current overall in-flight status. We stop polling once
    // it's terminal (complete / failed / no-action-found persisting after
    // a couple of polls). 'no-action-found' could be transient (digest
    // not yet started) so we treat it as in-flight initially.
    const overall = status?.overall ?? 'unknown';
    const isTerminal = overall === 'complete' || overall === 'failed';
    const refresh = useCallback(async () => {
        if (!projectId)
            return;
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get(`/p/${projectId}/documents/${documentId}/status`);
            setStatus(r.status);
            lastFetchAt.current = Date.now();
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to load status');
        }
        finally {
            setLoading(false);
        }
    }, [projectId, documentId]);
    // Initial fetch on mount.
    useEffect(() => {
        void refresh();
    }, [refresh]);
    // Polling loop: re-fetch every 30s while the window is open AND the
    // status isn't terminal. Skips while minimized (the pill UI doesn't
    // need fresh per-stage data; we'll catch up on restore).
    useEffect(() => {
        if (!win || win.minimized || isTerminal)
            return;
        const id = setInterval(() => {
            void refresh();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [win, win?.minimized, isTerminal, refresh]);
    if (!win)
        return null;
    // ---- Pill mode ----
    if (win.minimized) {
        const pose = pillPose(pillIndex);
        const label = status?.filename ?? win.filename ?? documentId.slice(0, 8);
        const stageLabel = isTerminal
            ? overall
            : status?.stages.find((s) => s.status === 'streaming')?.name ?? overall;
        return (_jsxs("button", { type: "button", className: "doc-status-pill", style: {
                position: 'fixed',
                right: pose.right,
                bottom: pose.bottom,
                width: PILL_W,
                height: PILL_H,
                zIndex: 1001000,
            }, onClick: () => setMinimized(documentId, false), title: `${label} -- ${stageLabel}`, children: [_jsx("span", { className: "doc-status-pill-glyph", children: pillGlyph(overall) }), _jsx("span", { className: "doc-status-pill-name", children: label }), _jsx("span", { className: "doc-status-pill-stage", children: stageLabel })] }));
    }
    // ---- Full window ----
    const filename = status?.filename ?? win.filename ?? '(unknown filename)';
    const action = status?.action ?? 'digest';
    return (_jsxs("div", { className: "doc-status-window", style: {
            position: 'fixed',
            left: pose.x,
            top: pose.y,
            width: WINDOW_W,
            zIndex: 1001000,
        }, role: "dialog", "aria-label": `Document status -- ${filename}`, children: [_jsxs("div", { className: "doc-status-header doc-status-header-draggable", onMouseDown: onHeaderMouseDown, children: [_jsxs("div", { className: "doc-status-title", children: [_jsx("span", { className: "doc-status-title-glyph", children: pillGlyph(overall) }), _jsx("span", { className: "doc-status-title-name", title: filename, children: filename })] }), _jsxs("div", { className: "doc-status-header-controls", children: [_jsx("button", { type: "button", className: "doc-status-ctl", onClick: () => setMinimized(documentId, true), title: "Minimize", children: '\u2014' }), _jsx("button", { type: "button", className: "doc-status-ctl", onClick: () => closeWindow(documentId, 'floating'), title: "Close", children: '\u2715' })] })] }), _jsxs("div", { className: "doc-status-summary", children: [_jsx("span", { className: "doc-status-action", children: action }), _jsx("span", { className: "doc-status-summary-sep", children: '\u00B7' }), _jsx("span", { className: `doc-status-overall doc-status-overall-${overall}`, children: overall }), status?.startedAt && (_jsxs(_Fragment, { children: [_jsx("span", { className: "doc-status-summary-sep", children: '\u00B7' }), _jsx("span", { className: "doc-status-summary-time", children: durationStr(status.startedAt, status.completedAt) })] }))] }), _jsx("div", { className: "doc-status-pipeline-note", children: "Stages run in parallel as data streams between them." }), err && _jsx("div", { className: "doc-status-error", children: err }), loading && !status && _jsx("div", { className: "doc-status-loading", children: 'Loading\u2026' }), status && (_jsxs("div", { className: "doc-status-stages", children: [status.stages.length === 0 && (_jsx("div", { className: "doc-status-stage-empty", children: overall === 'no-action-found'
                            ? 'Digest hasn\u2019t started yet.'
                            : 'No stages reported.' })), status.stages.map((stage, idx) => (_jsx(StageRowView, { stage: stage }, `${stage.name}-${idx}`)))] })), _jsxs("div", { className: "doc-status-footer", children: [_jsx("button", { type: "button", className: "doc-status-refresh", onClick: () => void refresh(), disabled: loading, title: "Refresh now", children: loading ? 'Refreshing\u2026' : 'Refresh' }), _jsx("span", { className: "doc-status-poll-hint", children: isTerminal
                            ? 'Done. Polling stopped.'
                            : `Auto-refresh every ${POLL_INTERVAL_MS / 1000}s.` })] })] }));
}
/**
 * Stage row with a 4-state vocabulary that matches the server's
 * effectiveStageStatus projection (see server/src/routes/documents.ts).
 * The server reads Phoenix's per-stage status + completedAt fields
 * DIRECTLY -- it does NOT gate completion on output presence.
 *
 *   complete   - relay reports status:complete OR completedAt is set
 *   streaming  - startedAt set, no completion signal yet
 *   queued     - startedAt null (Phoenix hasn't spawned the relay yet)
 *   failed     - relay status is failed
 *
 * The (i) info button is present per row whenever the stage has any
 * output payload at all -- toggles a JSON-pretty-printed panel for
 * the user to drill into. Phoenix's chunk stage drops its output at
 * job-end (output becomes empty {}); that doesn't change the stage's
 * complete status, just means the (i) panel won't show rich data
 * for that stage post-completion.
 */
function StageRowView({ stage }) {
    const [expanded, setExpanded] = useState(false);
    // Default to 'queued' (unknown / not started) when the server
    // didn't give us a status, NOT to 'streaming'. The previous default
    // would falsely paint stages as actively running just because their
    // status was missing -- exactly the kind of inference based on
    // anything-other-than-server-status that we want to avoid.
    const status = stage.status ?? 'queued';
    // Duration label per status:
    //   streaming -> "running for Xm Ys" (live counter, clear that
    //                multiple stages can be streaming simultaneously
    //                due to pipelining)
    //   complete  -> "Xm Ys" (frozen)
    //   queued    -> nothing (work hasn't started)
    const dur = stage.startedAt
        ? durationStr(stage.startedAt, stage.completedAt)
        : '';
    const durLabel = status === 'streaming' && dur ? `running for ${dur}` : dur;
    const hasOutput = !!stage.output
        && typeof stage.output === 'object'
        && Object.keys(stage.output).length > 0;
    return (_jsxs("div", { className: `doc-status-stage doc-status-stage-${status}`, children: [_jsxs("div", { className: "doc-status-stage-head", children: [_jsxs("span", { className: `doc-status-stage-pill doc-status-stage-pill-${status}`, children: [stageGlyph(status), " ", status] }), _jsx("span", { className: "doc-status-stage-name", children: stage.name }), durLabel && (_jsx("span", { className: "doc-status-stage-duration", children: durLabel })), hasOutput && (_jsx("button", { type: "button", className: "doc-status-stage-info", onClick: () => setExpanded((v) => !v), title: expanded ? 'Hide stage output' : 'Show stage output', "aria-expanded": expanded, children: '\u24D8' }))] }), hasOutput && (_jsx("div", { className: "doc-status-stage-output", children: formatOutput(stage.output) })), expanded && hasOutput && (_jsx("pre", { className: "doc-status-stage-output-json", children: JSON.stringify(stage.output, null, 2) }))] }));
}
/** Single-line output summary for the stage row -- pulls the most
 *  user-meaningful fields per stage type. Falls back to JSON for
 *  unknown shapes. */
function formatOutput(output) {
    if (!output || typeof output !== 'object')
        return '';
    const o = output;
    const parts = [];
    if (typeof o.pagesExtracted === 'number')
        parts.push(`${o.pagesExtracted} pages`);
    if (typeof o.totalChars === 'number')
        parts.push(`${o.totalChars.toLocaleString()} chars`);
    if (typeof o.chunkCount === 'number')
        parts.push(`${o.chunkCount} chunks`);
    if (typeof o.chunkMethod === 'string')
        parts.push(`(${o.chunkMethod})`);
    if (typeof o.embedTokens === 'number')
        parts.push(`${o.embedTokens.toLocaleString()} tokens`);
    if (typeof o.embedCost === 'number')
        parts.push(`$${o.embedCost.toFixed(6)}`);
    if (parts.length > 0)
        return parts.join(', ');
    // Fallback: show the keys we don't recognize so the user gets *something*.
    return Object.keys(o).slice(0, 3).join(', ');
}
function stageGlyph(status) {
    switch (status) {
        case 'complete': return '\u2713'; // check mark
        case 'streaming': return '\u25B6'; // play
        case 'failed': return '\u2717'; // ballot x
        case 'queued': return '\u22EF'; // midline horizontal ellipsis
        default: return '\u00B7';
    }
}
function pillGlyph(overall) {
    switch (overall) {
        case 'complete': return '\u2713';
        case 'failed': return '\u2717';
        case 'running': return '\u25B6';
        default: return '\u00B7';
    }
}
function durationStr(startedAt, completedAt) {
    if (!startedAt)
        return '';
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
        return '';
    const ms = end - start;
    const sec = Math.round(ms / 1000);
    if (sec < 60)
        return `${sec}s`;
    const min = Math.floor(sec / 60);
    const remSec = sec % 60;
    return remSec > 0 ? `${min}m ${remSec}s` : `${min}m`;
}
